import { useEffect, useState } from "react";
import { Star, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/** Public reviews carousel — auto-cycles through published reviews.
 *  Used on the landing page. Hides itself silently if no reviews. */
export function PublicReviewsCarousel() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reviews?limit=12");
        setReviews(data || []);
      } catch (_) {
        /* silent */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Auto-cycle every 8s
  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 8000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (!loaded || reviews.length === 0) return null;
  const r = reviews[idx];

  // Schema.org JSON-LD for SEO — surfaces aggregate stars in Google search results
  const avgRating = (reviews.reduce((s, x) => s + (x.rating || 0), 0) / reviews.length).toFixed(1);
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Vision Grafix",
    url: "https://vzngfx.com",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: reviews.length,
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((rv) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(rv.rating),
        bestRating: "5",
        worstRating: "1",
      },
      author: { "@type": "Person", name: rv.display_company || rv.client_name || "Client" },
      reviewBody: rv.body,
      ...(rv.created_at ? { datePublished: rv.created_at.slice(0, 10) } : {}),
    })),
  };

  return (
    <section className="bg-[#050505] py-24 sm:py-32 border-t border-[#262626]" data-testid="reviews-carousel">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] text-center">What clients say</div>
        <h2 className="font-display text-4xl sm:text-5xl mt-3 text-center">In their own words.</h2>

        <div className="mt-12 relative" data-testid="review-slide">
          <div className="border border-[#262626] bg-[#0a0a0a] p-8 sm:p-12">
            <div className="flex gap-1 mb-5" aria-label={`${r.rating} out of 5 stars`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={20} weight="fill" className={n <= r.rating ? "text-[#FF2D2D]" : "text-[#262626]"} />
              ))}
            </div>
            <blockquote className="text-xl sm:text-2xl font-display leading-snug">
              &ldquo;{r.body}&rdquo;
            </blockquote>
            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-semibold">{r.display_company || r.client_name}</div>
                {r.service_type && (
                  <div className="text-xs text-[#a3a3a3] capitalize mt-0.5">{r.service_type.replace(/_/g, " ")}{r.project_title ? ` · ${r.project_title}` : ""}</div>
                )}
              </div>
              <div className="text-xs text-[#666]">{idx + 1} / {reviews.length}</div>
            </div>
          </div>

          {reviews.length > 1 && (
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
                className="p-2 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                aria-label="Previous review"
                data-testid="review-prev"
              >
                <ArrowLeft size={14} />
              </button>
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to review ${i + 1}`}
                  className={`w-2 h-2 transition-colors ${i === idx ? "bg-[#FF2D2D]" : "bg-[#262626] hover:bg-[#666]"}`}
                />
              ))}
              <button
                onClick={() => setIdx((i) => (i + 1) % reviews.length)}
                className="p-2 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                aria-label="Next review"
                data-testid="review-next"
              >
                <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
